import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react'

const CV_SETTINGS_STORAGE_KEY = 'cvOverviewSettings'

export const DEFAULT_CV_SETTINGS = {
  cv_base: 'word',
  work_experience: 'htp',
  cv_language: 'fi' as 'fi' | 'en',

  inc_title: true,
  inc_email: true,
  inc_picture: true,
  inc_summary: true,
  inc_workexperiencedescription: true,
  inc_workDescription: false,

  inc_knowhow: true,
  showExperienceDuration: false,
  inc_keyexpertise: true,
  inc_showroles: false,
  inc_keyroles: false,

  inc_education: true,
  inc_eduschool: true,
  inc_edufinish: false,
  inc_eduinfo: false,
  educationOption: 'key',

  inc_certificates: true,
  inc_certissuer: true,
  inc_certvalidity: false,
  inc_certdetails: false,
  inc_certshowexpired: false,

  inc_workhistory: true,
  inc_workhistory_keyonly: true,
  inc_workhistory_jobtitle: true,
  inc_workhistory_timeframe: true,
  inc_workhistory_duration: true,
  inc_workhistory_industry: false,
  inc_workhistory_persondescription: true,
  inc_workhistory_personskills: false,
  inc_workhistory_personroles: false,

  inc_projects: true,
  inc_keyprojects: true,
  inc_netumonly: true,
  inc_bundlesubprojects: false,
  inc_employer: false,
  inc_projectemployer: false,
  inc_projectclientname: false,
  inc_projectclientcontact: false,
  inc_projectduration: true,
  inc_projecttimeframe: true,
  inc_projectindustry: false,
  inc_projectdescription: false,
  inc_persondescription: true,
  inc_personskills: false,
  inc_bundleskills: false,
  inc_personroles: false,
  inc_skillsexperience: false,
  inc_rolesexperience: false,
  inc_skillsandroles: false,

  inc_skills: true,
  inc_keyskills: true,
  inc_skillscategories: true,
  inc_skillslevel: true,
  inc_skillskillsexperience: true,
  inc_skillslastused: true,
  inc_skillsdescriptions: false,
  inc_skillsleveldescriptions: false,

  inc_languages: true,
  inc_languagelevel: true,
  inc_languagedescription: false,

  // PowerPoint-specific settings
  pptx_inc_title: true,
  pptx_inc_email: true,
  pptx_inc_picture: true,
  pptx_inc_summary: true,
  pptx_inc_workexperiencedescription: true,

  pptx_inc_knowhow: true,
  pptx_inc_showskills: true,
  pptx_inc_keyskills: true,
  pptx_inc_showroles: false,
  pptx_inc_keyroles: false,
  pptx_inc_experiencetime: false,

  pptx_inc_education: true,
  pptx_inc_edu_highestonly: true,
  pptx_inc_edu_keyonly: false,
  pptx_inc_eduschool: false,
  pptx_inc_edufinish: true,

  pptx_inc_certificates: true,
  pptx_inc_certissuer: false,

  pptx_inc_workhistory: true,
  pptx_inc_workhistory_keyonly: true,
  pptx_inc_workhistory_jobtitle: true,
  pptx_inc_workhistory_timeframe: true,

  pptx_inc_projects: true,
  pptx_inc_keyprojects: true,
  pptx_inc_netumonly: true,
  pptx_inc_projectclientname: true,
}

export type CVSettings = typeof DEFAULT_CV_SETTINGS

type CVSettingsContextType = {
  state: typeof DEFAULT_CV_SETTINGS
  handleSwitchChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  resetToDefaultSettings: () => void
}

const CVSettingsContext = createContext<CVSettingsContextType | undefined>(undefined)

export function useCVSettings() {
  const context = useContext(CVSettingsContext)
  if (context === undefined) {
    throw new Error('useCVSettings must be used within a CVSettingsProvider')
  }
  return context
}

export function CVSettingsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState(() => {
    // Try to load saved settings from localStorage
    try {
      const savedSettings = localStorage.getItem(CV_SETTINGS_STORAGE_KEY)
      if (savedSettings) {
        return JSON.parse(savedSettings) as typeof DEFAULT_CV_SETTINGS
      }
    } catch (error) {
      console.error('Error loading CV settings from localStorage:', error)
    }

    return { ...DEFAULT_CV_SETTINGS }
  })

  useEffect(() => {
    try {
      localStorage.setItem(CV_SETTINGS_STORAGE_KEY, JSON.stringify(state))
    } catch (error) {
      console.error('Error saving CV settings to localStorage:', error)
    }
  }, [state])

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name
    const checked = event.target.checked

    setState((prevState) => ({
      ...prevState,
      [name]: checked,
    }))
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name
    const value = event.target.value

    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const resetToDefaultSettings = () => {
    setState((prevState) => {
      const isPowerPoint = prevState.cv_base === 'pptx'
      const newState = { ...prevState }

      if (isPowerPoint) {
        // Reset only PowerPoint-specific settings
        Object.keys(DEFAULT_CV_SETTINGS).forEach((key) => {
          if (key.startsWith('pptx_')) {
            const typedKey = key as keyof typeof DEFAULT_CV_SETTINGS
            ;(newState as any)[typedKey] = DEFAULT_CV_SETTINGS[typedKey]
          }
        })
      } else {
        // Reset only non-PowerPoint settings (excluding template/language selection settings)
        Object.keys(DEFAULT_CV_SETTINGS).forEach((key) => {
          if (!key.startsWith('pptx_') && key !== 'cv_base' && key !== 'work_experience' && key !== 'cv_language') {
            const typedKey = key as keyof typeof DEFAULT_CV_SETTINGS
            ;(newState as any)[typedKey] = DEFAULT_CV_SETTINGS[typedKey]
          }
        })
      }

      localStorage.setItem(CV_SETTINGS_STORAGE_KEY, JSON.stringify(newState))
      return newState
    })
  }

  const value = {
    state,
    handleSwitchChange,
    handleChange,
    resetToDefaultSettings,
  }

  return <CVSettingsContext.Provider value={value}>{children}</CVSettingsContext.Provider>
}
